import { createScopedLogger, IconComponent, useErrorHandler } from "@@common";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import React, { useState } from "react";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useRegisterViewController from "../../Hooks/useRegisterViewController";
import useViewOnOpen from "../../Hooks/useViewOnOpen";
import { DialogActionModel } from "../../Models/DialogActionModel";
import { ViewModel } from "../../Models/ViewModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import getComponentLayout from "../Table/getComponentLayout";
import DialogActionButton from "./DialogActionButton";
import useViewExpressionEvaluatorContext from "./useViewExpressionEvaluatorContext";
import ViewBodyComponent from "./ViewBodyComponent";

type ViewComponentProps = {
  component: ViewModel;
  open?: boolean;
  input?: Record<string, any>;
  setOutput?(output: Record<string, any> | undefined): void;
};

const DIALOG_MAX_WIDTH: Record<string, "xs" | "sm" | "md" | "lg" | "xl"> = {
  Xs: "xs",
  Sm: "sm",
  Md: "md",
  Lg: "lg",
  Xl: "xl",
};
const logger = createScopedLogger("ViewComponent");

const ViewComponent: React.FC<ViewComponentProps> = ({ component, open, input, setOutput }) => {
  logger.debug("Rendering with input:", component.Id, input, "Dialog:", component.Dialog, "Actions:", component.Dialog?.Actions);
  const viewContext = useViewExpressionEvaluatorContext(component, input);

  useRegisterViewController(component, viewContext, undefined, setOutput);

  const [onOpenResult, onOpenPending] = useViewOnOpen(component, viewContext, "ViewComponent");

  const [actionPending, setActionPending] = useState<number | null>(null);

  const errorHandler = useErrorHandler();
  const handleAction = async (actionKey: number, action: DialogActionModel) => {
    if (!action.OnSelected) {
      setOutput?.(undefined);
      return;
    }

    setActionPending(actionKey);
    try {
      var result = await viewContext.Evaluator.evaluate(action.OnSelected, viewContext);
      setOutput?.(result);
    } catch (error) {
      errorHandler(error);
    } finally {
      setActionPending(null);
    }
  };

  const componentLayoutSx = getComponentLayout(component);

  // DialogActions container selection props (called unconditionally for hooks rules)
  const dialogActionsSelectionProps = useComponentSelection({
    component,
    componentPath: ["actions"],
    isContainer: true,
    isEmpty: !component.Dialog?.Actions || component.Dialog.Actions.length === 0,
  });

  if (component.Dialog) {
    return (
      <Dialog
        open={open ?? true}
        onClose={() => setOutput?.(undefined)}
        fullScreen={component.Dialog.FullScreen}
        fullWidth={component.Dialog.FullWidth}
        maxWidth={component.Dialog.MaxWidth ? DIALOG_MAX_WIDTH[component.Dialog.MaxWidth] : "xl"}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1}>
              {component.Dialog.IconName && <IconComponent iconName={component.Dialog.IconName} />}
              {component.Title ?? "Dialog"}
            </Stack>
            <IconButton onClick={() => setOutput?.(undefined)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ViewBodyComponent
            isPending={onOpenPending}
            component={component}
            children={component.Children}
            context={viewContext}
            sx={componentLayoutSx}
            componentPath={[]}
          />
        </DialogContent>
        {(IsDesignMode || (component.Dialog.Actions && component.Dialog.Actions.length > 0)) && (
          <DialogActions {...dialogActionsSelectionProps}>
            {component.Dialog.Actions?.map((action, index) => (
              <DialogActionButton
                key={index}
                action={action}
                actionIndex={index}
                viewId={component.Id}
                componentPath={["actions", String(index)]}
                loading={actionPending === index}
                disabled={actionPending !== null}
                onClick={() => handleAction(index, action)}
                context={viewContext}
              />
            ))}
          </DialogActions>
        )}
      </Dialog>
    );
  }

  return (
    <ViewBodyComponent
      isPending={onOpenPending}
      component={component}
      children={component.Children}
      context={viewContext}
      sx={componentLayoutSx}
      componentPath={[]}
    />
  );
};

export default ViewComponent;
